import { cloneDeep } from 'lodash';

import type { LoginSocialBarSdkData } from '@wix/thunderbolt-components';
import {
  withValidation,
  composeSDKFactories,
  resolveAndFetchSvg,
  createElementPropsSDKFactory,
  registerCorvidEvent,
  toJSONBase,
} from '@wix/editor-elements-corvid-utils';
import type { LoginSocialBarSdkDataCommonProps } from '@wix/thunderbolt-components-native';
import type {
  IComponentEvent,
  ICorvidEvent,
  ICorvidSelector,
} from '@wix/editor-elements-types/corvid';

import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';

import type {
  BuyAgainAddToCartEventPayload,
  BuyAgainOrderItem,
  ILoginSocialBarProps,
  ILoginSocialBarOwnSDKFactory,
  ILoginSocialBarSDK,
  ILoginSocialBarSDKMenuItems,
  ILoginSocialBarSDKNavBarItems,
  LoginSocialBarMenuItemProps,
  OnBuyAgainAddToCartEvent,
} from '../LoginSocialBar.types';
import {
  CartClickAction,
  ON_BUY_AGAIN_ADD_TO_CART_EVENT,
} from '../LoginSocialBar.types';

import { ELEMENT_TYPE } from './constants';
import {
  menuItemsPropertySchema,
  navBarItemsPropertySchema,
} from './validationSchemas';
import { validateNavBarItems } from './customValidators';

type CreateAccountNavBarSdkFactoryParams = {
  isMobileOnly?: boolean;
};

type ICommonItem = Partial<
  Pick<
    LoginSocialBarSdkDataCommonProps,
    'link' | 'visibleOnDesktop' | 'visibleOnMobile' | 'displayCount'
  >
>;

const getCommonOptionalItemProps = (
  { link, visibleOnDesktop, visibleOnMobile, displayCount }: ICommonItem,
  isMobileOnly: boolean,
) => ({
  ...(link !== undefined && { link }),
  ...(!isMobileOnly && { visibleOnDesktop: visibleOnDesktop ?? true }),
  visibleOnMobile: visibleOnMobile ?? true,
  ...(typeof displayCount === 'number' && { displayCount }),
});

const cloneMenuItems = (
  menuItems: ILoginSocialBarSDKMenuItems,
  isMobileOnly: boolean,
): ILoginSocialBarSDKMenuItems =>
  menuItems.map(
    ({ label, linkMetaData, items, ...optionalProps }) =>
      ({
        label,
        ...(linkMetaData !== undefined && {
          linkMetaData: cloneDeep(linkMetaData),
        }),
        items: items?.length ? cloneMenuItems(items, isMobileOnly) : [],
        ...getCommonOptionalItemProps(optionalProps, isMobileOnly),
      }) as ILoginSocialBarSDKMenuItems[0],
  );

const cloneNavBarItems = (
  navBarItems: ILoginSocialBarSDKNavBarItems,
  isMobileOnly: boolean,
) =>
  navBarItems.map(({ icon, label, linkMetaData, ...optionalProps }) => ({
    icon,
    ...(label !== undefined && { label }),
    ...(linkMetaData !== undefined && {
      linkMetaData: cloneDeep(linkMetaData),
    }),
    ...getCommonOptionalItemProps(optionalProps, isMobileOnly),
  }));

const createSdkFactory =
  ({
    isMobileOnly = false,
  }: CreateAccountNavBarSdkFactoryParams): ILoginSocialBarOwnSDKFactory =>
  api => {
    const { setProps, props, sdkData, metaData, createSdkState, handlers } =
      api;
    const { isMobileView = isMobileOnly, baseSvgMediaUrl } = sdkData;
    const isItemVisiblePredicate = ({
      visibleOnDesktop,
      visibleOnMobile,
    }: ICommonItem) => (isMobileView ? visibleOnMobile : visibleOnDesktop);

    const [state, setState] = createSdkState<{
      menuItems: ILoginSocialBarSDKMenuItems;
      navBarItems: ILoginSocialBarSDKNavBarItems;
      buyAgainConfirmSeq: number;
    }>({
      menuItems: sdkData.menuItems,
      navBarItems: sdkData.navBarItems,
      buyAgainConfirmSeq: 0,
    });

    return {
      get menuItems() {
        return cloneMenuItems(state.menuItems, isMobileOnly);
      },

      set menuItems(value) {
        setState({
          menuItems: value ? cloneMenuItems(value, isMobileOnly) : [],
        });

        const toMenuItemProps = (
          items: ILoginSocialBarSDKMenuItems,
        ): Array<LoginSocialBarMenuItemProps> =>
          items
            .filter(isItemVisiblePredicate)
            .map(
              ({
                label,
                link,
                linkMetaData,
                displayCount,
                items: childItems,
              }) => ({
                label,
                link: {
                  ...(link && { href: link }),
                  ...cloneDeep(linkMetaData),
                },
                ...(displayCount !== undefined && { displayCount }),
                items: childItems?.length ? toMenuItemProps(childItems) : [],
              }),
            );

        setProps({ menuItems: toMenuItemProps(state.menuItems) });
      },

      get navBarItems() {
        return cloneNavBarItems(state.navBarItems, isMobileOnly);
      },

      set navBarItems(value) {
        setState({
          navBarItems: value ? cloneNavBarItems(value, isMobileOnly) : [],
        });
        const visibleNavBarItems = state.navBarItems.filter(
          isItemVisiblePredicate,
        );

        if (!visibleNavBarItems.length) {
          setProps({ iconItems: [] });
          return;
        }

        setProps(
          Promise.all(
            visibleNavBarItems.map(({ icon }) =>
              resolveAndFetchSvg(icon, baseSvgMediaUrl, handlers.sanitizeSVG),
            ),
          ).then(icons => ({
            iconItems: visibleNavBarItems.map(
              ({ label, link, linkMetaData, displayCount }, index) => ({
                iconSvgContent: icons[index],
                label: label ?? '',
                link: {
                  ...(link ? { href: link } : {}),
                  ...cloneDeep(linkMetaData),
                },
                displayCount,
              }),
            ),
          })),
        );
      },
      get buyAgainSettings() {
        return {
          showBuyAgain: props.showBuyAgain ?? false,
          buyAgainCount: props.buyAgainCount ?? 1,
          cartClickAction: props.cartClickAction ?? CartClickAction.SideCart,
        };
      },

      get buyAgainOrderItems() {
        return [...(props.buyAgainOrderItems ?? [])];
      },

      set buyAgainOrderItems(value: Array<BuyAgainOrderItem>) {
        setProps({ buyAgainOrderItems: value ?? [] });
      },

      onBuyAgainAddToCart(
        handler: (
          event: ICorvidEvent<
            OnBuyAgainAddToCartEvent,
            BuyAgainAddToCartEventPayload
          >,
          $w: ICorvidSelector,
        ) => void,
      ) {
        return registerCorvidEvent<
          IComponentEvent<
            OnBuyAgainAddToCartEvent,
            BuyAgainAddToCartEventPayload
          >,
          ICorvidEvent<OnBuyAgainAddToCartEvent, BuyAgainAddToCartEventPayload>
        >(
          ON_BUY_AGAIN_ADD_TO_CART_EVENT,
          api,
          handler,
          ({ componentEvent }) => ({
            catalogItemId: componentEvent.catalogItemId,
            catalogOptions: componentEvent.catalogOptions,
          }),
        );
      },

      confirmBuyAgainItemAdded(catalogItemId: string) {
        const seq = state.buyAgainConfirmSeq + 1;
        setState({ buyAgainConfirmSeq: seq });
        setProps({
          buyAgainConfirmedItem: { catalogItemId, seq },
        });
      },

      get type() {
        return ELEMENT_TYPE;
      },

      toJSON() {
        return {
          ...toJSONBase(metaData),
          type: ELEMENT_TYPE,
        };
      },
    };
  };

export const createAccountNavBarSdkFactory = (
  params: CreateAccountNavBarSdkFactoryParams = {},
) =>
  withValidation(
    createSdkFactory(params),
    {
      type: ['object'],
      properties: {
        menuItems: menuItemsPropertySchema,
        navBarItems: navBarItemsPropertySchema,
        buyAgainOrderItems: {
          type: ['array'],
          items: {
            type: ['object'],
            properties: {
              catalogItemId: { type: ['string'] },
              productName: { type: ['string'] },
              formattedPrice: { type: ['string'] },
              imageUrl: { type: ['string', 'nil'] },
              catalogOptions: { type: ['object', 'nil'] },
            },
            required: ['catalogItemId', 'productName', 'formattedPrice'],
          },
        },
      },
    },
    {
      navBarItems: [validateNavBarItems],
    },
  );

const loginSocialBarSDKFactory = createAccountNavBarSdkFactory();
const elementPropsSDKFactory = createElementPropsSDKFactory();

export const sdk = composeSDKFactories<
  ILoginSocialBarProps,
  ILoginSocialBarSDK,
  LoginSocialBarSdkData
>([elementPropsSDKFactory, loginSocialBarSDKFactory]);

export default createComponentSDKModel(sdk);
